/**
 * PhoneWidget — Browser Softphone UI
 * 
 * Floating phone widget that enables incoming/outgoing calls
 * directly from the AlwaysIn dashboard.
 * 
 * States:
 * 1. FAB (minimized) — phone icon, click to expand
 * 2. Idle — dial pad + call button
 * 3. Ringing — incoming call notification with answer/reject
 * 4. Active Call — timer, mute, keypad, hangup
 * 
 * @module components/phone/PhoneWidget
 */

import { useState, useCallback } from 'react';
import { usePhone, PHONE_STATUS } from '../../hooks/usePhone';
import './phone.css';

// ─── Icons (inline SVG) ─────────────────────────────────────

const PhoneIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
    </svg>
);

const HangUpIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.68 13.31a16 16 0 003.41 2.6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91"/>
        <line x1="23" y1="1" x2="1" y2="23"/>
    </svg>
);

const MicIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/>
        <path d="M19 10v2a7 7 0 01-14 0v-2"/>
        <line x1="12" y1="19" x2="12" y2="23"/>
        <line x1="8" y1="23" x2="16" y2="23"/>
    </svg>
);

const MicOffIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="1" y1="1" x2="23" y2="23"/>
        <path d="M9 9v3a3 3 0 005.12 2.12M15 9.34V4a3 3 0 00-5.94-.6"/>
        <path d="M17 16.95A7 7 0 015 12v-2m14 0v2c0 .64-.09 1.26-.25 1.84"/>
        <line x1="12" y1="19" x2="12" y2="23"/>
        <line x1="8" y1="23" x2="16" y2="23"/>
    </svg>
);

const KeypadIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="4" height="4" rx="1"/>
        <rect x="10" y="3" width="4" height="4" rx="1"/>
        <rect x="17" y="3" width="4" height="4" rx="1"/>
        <rect x="3" y="10" width="4" height="4" rx="1"/>
        <rect x="10" y="10" width="4" height="4" rx="1"/>
        <rect x="17" y="10" width="4" height="4" rx="1"/>
        <rect x="10" y="17" width="4" height="4" rx="1"/>
    </svg>
);

const CloseIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
);

// ─── Keypad Data ────────────────────────────────────────────

const KEYS = [
    { digit: '1', letters: '' },
    { digit: '2', letters: 'ABC' },
    { digit: '3', letters: 'DEF' },
    { digit: '4', letters: 'GHI' },
    { digit: '5', letters: 'JKL' },
    { digit: '6', letters: 'MNO' },
    { digit: '7', letters: 'PQRS' },
    { digit: '8', letters: 'TUV' },
    { digit: '9', letters: 'WXYZ' },
    { digit: '*', letters: '' },
    { digit: '0', letters: '+' },
    { digit: '#', letters: '' },
];

// ─── Format call duration ───────────────────────────────────

function formatDuration(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

// ═══════════════════════════════════════════════════════════
// PHONE WIDGET COMPONENT
// ═══════════════════════════════════════════════════════════

export default function PhoneWidget() {
    const phone = usePhone();
    const [isOpen, setIsOpen] = useState(false);
    const [dialNumber, setDialNumber] = useState('');
    const [showKeypadInCall, setShowKeypadInCall] = useState(false);

    // Auto-open on incoming call
    const shouldShow = isOpen || phone.status === PHONE_STATUS.RINGING;

    // ─── Dial pad key press ─────────────────────────────
    const handleKeyPress = useCallback((digit) => {
        if (phone.activeCall) {
            phone.sendDigits(digit);
        } else {
            setDialNumber(prev => prev + digit);
        }
    }, [phone]);

    // ─── Make outbound call ─────────────────────────────
    const handleDial = useCallback(async () => {
        if (!dialNumber.trim()) return;
        try {
            await phone.makeCall(dialNumber.trim());
        } catch (err) {
            console.error('Call failed:', err);
        }
    }, [dialNumber, phone]);

    // ─── Status dot class ───────────────────────────────
    const statusClass = {
        [PHONE_STATUS.READY]: 'phone-widget__status--ready',
        [PHONE_STATUS.CONNECTING]: 'phone-widget__status--connecting',
        [PHONE_STATUS.IN_CALL]: 'phone-widget__status--in-call',
        [PHONE_STATUS.ERROR]: 'phone-widget__status--error',
    }[phone.status] || '';

    return (
        <>
            {/* Floating Action Button */}
            <button
                className={`phone-fab ${phone.status === PHONE_STATUS.RINGING ? 'phone-fab--ringing' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
                title="Phone"
                id="phone-fab-btn"
            >
                <PhoneIcon />
                {phone.incomingCall && <span className="phone-fab__badge" />}
            </button>

            {/* Widget Panel */}
            {shouldShow && (
                <div className="phone-widget" id="phone-widget-panel">
                    {/* Header */}
                    <div className="phone-widget__header">
                        <div className="phone-widget__title">
                            <span className={`phone-widget__status ${statusClass}`} />
                            Phone
                        </div>
                        <button
                            className="phone-widget__close"
                            onClick={() => setIsOpen(false)}
                            title="Close"
                        >
                            <CloseIcon />
                        </button>
                    </div>

                    <div className="phone-widget__body">
                        {/* Error Display */}
                        {phone.error && (
                            <div className="phone-error">{phone.error}</div>
                        )}

                        {/* ─── Not Configured ──────────── */}
                        {!phone.isAvailable && (
                            <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--text-secondary, #6b7280)', fontSize: '13px', lineHeight: '1.6' }}>
                                <div style={{ fontSize: '28px', marginBottom: '8px' }}>📞</div>
                                <strong style={{ color: 'var(--text-primary, #111)' }}>Voice Calling Not Configured</strong>
                                <p style={{ marginTop: '8px', marginBottom: 0 }}>
                                    Add <code>TWILIO_API_KEY</code> and <code>TWILIO_API_SECRET</code> to your server .env and restart.
                                </p>
                            </div>
                        )}

                        {/* ─── Offline: Init Button ──────── */}
                        {phone.isAvailable && (phone.status === PHONE_STATUS.OFFLINE || phone.status === PHONE_STATUS.ERROR) && (
                            <button
                                className="phone-init-btn"
                                onClick={phone.initialize}
                                disabled={phone.status === PHONE_STATUS.CONNECTING}
                                id="phone-init-btn"
                            >
                                <PhoneIcon />
                                {phone.status === PHONE_STATUS.ERROR ? 'Retry Connection' : 'Start Phone'}
                            </button>
                        )}

                        {/* ─── Connecting ────────────────── */}
                        {phone.status === PHONE_STATUS.CONNECTING && (
                            <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-secondary)' }}>
                                Connecting...
                            </div>
                        )}

                        {/* ─── Incoming Call ─────────────── */}
                        {phone.status === PHONE_STATUS.RINGING && phone.incomingCall && (
                            <div className="phone-incoming">
                                <div className="phone-incoming__label">Incoming Call</div>
                                <div className="phone-incoming__number" id="phone-incoming-number">
                                    {phone.callerInfo?.number || 'Unknown'}
                                </div>
                                <div className="phone-incoming__actions">
                                    <button
                                        className="phone-incoming__btn phone-incoming__btn--answer"
                                        onClick={phone.answerCall}
                                        title="Answer"
                                        id="phone-answer-btn"
                                    >
                                        <PhoneIcon />
                                    </button>
                                    <button
                                        className="phone-incoming__btn phone-incoming__btn--reject"
                                        onClick={phone.rejectCall}
                                        title="Reject"
                                        id="phone-reject-btn"
                                    >
                                        <HangUpIcon />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ─── Active Call ───────────────── */}
                        {phone.status === PHONE_STATUS.IN_CALL && (
                            <div className="phone-active">
                                <div className="phone-active__direction">
                                    {phone.callerInfo?.direction === 'inbound' ? 'Incoming' : 'Outgoing'} Call
                                </div>
                                <div className="phone-active__number" id="phone-active-number">
                                    {phone.callerInfo?.number || 'Unknown'}
                                </div>
                                <div className="phone-active__timer" id="phone-call-timer">
                                    {formatDuration(phone.callDuration)}
                                </div>

                                {/* In-call keypad */}
                                {showKeypadInCall && (
                                    <div className="phone-keypad" style={{ marginBottom: 12 }}>
                                        {KEYS.map(k => (
                                            <button
                                                key={k.digit}
                                                className="phone-keypad__btn"
                                                onClick={() => handleKeyPress(k.digit)}
                                            >
                                                <span className="phone-keypad__digit">{k.digit}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}

                                <div className="phone-active__controls">
                                    <button
                                        className={`phone-active__control-btn ${phone.isMuted ? 'phone-active__control-btn--active' : ''}`}
                                        onClick={phone.toggleMute}
                                        title={phone.isMuted ? 'Unmute' : 'Mute'}
                                        id="phone-mute-btn"
                                    >
                                        {phone.isMuted ? <MicOffIcon /> : <MicIcon />}
                                    </button>
                                    <button
                                        className={`phone-active__control-btn ${showKeypadInCall ? 'phone-active__control-btn--active' : ''}`}
                                        onClick={() => setShowKeypadInCall(!showKeypadInCall)}
                                        title="Keypad"
                                        id="phone-keypad-toggle-btn"
                                    >
                                        <KeypadIcon />
                                    </button>
                                </div>

                                <button
                                    className="phone-call-btn phone-call-btn--hangup"
                                    onClick={phone.hangUp}
                                    id="phone-hangup-btn"
                                >
                                    <HangUpIcon /> End Call
                                </button>
                            </div>
                        )}

                        {/* ─── Dial Pad (Ready state) ────── */}
                        {phone.status === PHONE_STATUS.READY && (
                            <>
                                <input
                                    type="tel"
                                    className="phone-dial-input"
                                    value={dialNumber}
                                    onChange={e => setDialNumber(e.target.value)}
                                    placeholder="Enter number..."
                                    onKeyDown={e => e.key === 'Enter' && handleDial()}
                                    id="phone-dial-input"
                                />

                                <div className="phone-keypad">
                                    {KEYS.map(k => (
                                        <button
                                            key={k.digit}
                                            className="phone-keypad__btn"
                                            onClick={() => handleKeyPress(k.digit)}
                                        >
                                            <span className="phone-keypad__digit">{k.digit}</span>
                                            {k.letters && <span className="phone-keypad__letters">{k.letters}</span>}
                                        </button>
                                    ))}
                                </div>

                                <button
                                    className="phone-call-btn phone-call-btn--dial"
                                    onClick={handleDial}
                                    disabled={!dialNumber.trim()}
                                    id="phone-call-btn"
                                >
                                    <PhoneIcon /> Call
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
